"use client";
import { useEffect, useRef, useState } from "react";

import gsap from "gsap";
import CustomEase from "gsap/dist/CustomEase";
import { useTransitionRouter } from "next-view-transitions";
import Letstalk from "../header/LetsTalk";
import { RiMenu3Line, RiCloseLine, RiArrowDownSLine } from "react-icons/ri";

const services = [
  { label: "UI/UX design", href: "/ui-ux" },
  { label: "Web development and SEO", href: "/ui-ux" },
  { label: "Testing and Consulting", href: "/ui-ux" },
  { label: "Maintenance & Support", href: "/ui-ux" },
];

const industries = [
  { label: "Business & Professional", href: "/industry" },
  { label: "Commerce & Hospitality", href: "/industry" },
  { label: "Technology & Digital", href: "/industry" },
  { label: "Property & Construction", href: "/industry" },
  { label: "Health & Lifestyle", href: "/industry" },
  { label: "Education & Public", href: "/industry" },
];

const NAV_LINKS = [
  { label: "Services", type: "dropdown", items: services },
  { label: "Case Studies", href: "/studies" },
  { label: "Industry", type: "dropdown", items: industries },
  { label: "Digital Trends", href: "/trends" },
  { label: "About", href: "/about-us" },
  { label: "Contact", href: "/contact-us" },
];

const Nav = () => {
  const router = useTransitionRouter();

  const [isOpen, setIsOpen] = useState(false);
  const [openSection, setOpenSection] = useState(null);

  const navRef = useRef(null);
  const menuOverlayRef = useRef(null);
  const menuOpenTextRef = useRef(null);
  const menuCloseTextRef = useRef(null);

  function slideInOut() {
    document.documentElement.animate(
      [
        { opacity: 1, transform: "scale(1)" },
        { opacity: 0.4, transform: "scale(0.5)" },
      ],
      {
        duration: 1500,
        easing: "cubic-bezier(0.87, 0, 0.13, 1)",
        fill: "forwards",
        pseudoElement: "::view-transition-old(root)",
      }
    );

    document.documentElement.animate(
      [
        { clipPath: "polygon(0% 100%, 100% 100%, 100% 100%, 0% 100%)" },
        { clipPath: "polygon(0% 100%, 100% 100%, 100% 0%, 0% 0%)" },
      ],
      {
        duration: 1500,
        easing: "cubic-bezier(0.87, 0, 0.13, 1)",
        fill: "forwards",
        pseudoElement: "::view-transition-new(root)",
      }
    );
  }

  const handleCloseMenu = () => {
    if (!navRef.current || !menuOverlayRef.current) return;

    if (menuCloseTextRef.current) {
      gsap.to(menuCloseTextRef.current, {
        y: "-100%",
        duration: 1,
        ease: CustomEase.create("", ".76,0,.2,1"),
        onComplete: () => {
          if (menuCloseTextRef.current) {
            gsap.set(menuCloseTextRef.current, { y: "100%" });
          }
        },
      });
    }

    gsap.to(".menu-link-item", {
      y: "-100%",
      duration: 0.75,
      stagger: 0.05,
      ease: "power4.in",
      onComplete: () => {
        gsap.set(".menu-link-item", { y: "100%" });
      },
    });

    gsap.to(menuOverlayRef.current, {
      clipPath: "polygon(0% 0%, 100% 0%, 100% 0%, 0% 0%)",
      duration: 1,
      delay: 0.5,
      ease: CustomEase.create("", ".76,0,.2,1"),
      onComplete: () => {
        setIsOpen(false);
        setOpenSection(null);
        if (menuOverlayRef.current) {
          gsap.set(menuOverlayRef.current, {
            pointerEvents: "none",
            clipPath: "polygon(0% 100%, 100% 100%, 100% 100%, 0% 100%)",
          });
        }
      },
    });

    if (menuOpenTextRef.current) {
      gsap.to(menuOpenTextRef.current, {
        y: "0%",
        duration: 1,
        delay: 0.5,
        ease: CustomEase.create("", ".76,0,.2,1"),
      });
    }
  };

  const handleOpenMenu = () => {
    if (!navRef.current || !menuOverlayRef.current) return;

    setIsOpen(true);

    if (menuOpenTextRef.current) {
      gsap.to(menuOpenTextRef.current, {
        y: "-100%",
        duration: 1,
        ease: CustomEase.create("", ".76,0,.2,1"),
        onComplete: () => {
          if (menuOpenTextRef.current) {
            gsap.set(menuOpenTextRef.current, { y: "100%" });
          }
        },
      });
    }

    gsap.to(menuOverlayRef.current, {
      clipPath: "polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)",
      duration: 1,
      ease: CustomEase.create("", ".76,0,.2,1"),
      onComplete: () => {
        if (menuOverlayRef.current) {
          gsap.set(menuOverlayRef.current, { pointerEvents: "all" });
        }
      },
    });

    gsap.to(".menu-link-item", {
      y: "0%",
      duration: 1,
      stagger: 0.1,
      delay: 0.5,
      ease: "power3.out",
    });

    if (menuCloseTextRef.current) {
      gsap.to(menuCloseTextRef.current, {
        y: "0%",
        duration: 1,
        delay: 0.5,
        ease: CustomEase.create("", ".76,0,.2,1"),
      });
    }
  };

  useEffect(() => {
    gsap.registerPlugin(CustomEase);

    router.events?.on("routeChangeStart", handleCloseMenu);

    return () => {
      router.events?.off("routeChangeStart", handleCloseMenu);
    };
  }, [router]);

  const handleToggleClick = () => {
    if (isOpen) {
      handleCloseMenu();
    } else {
      handleOpenMenu();
    }
  };

  const handleNavigation = (e, path) => {
    e.preventDefault();

    const currentPath =
      typeof window !== "undefined" ? window.location.pathname : "/";

    if (currentPath === path) {
      handleCloseMenu();
      return;
    }

    handleCloseMenu();

    setTimeout(() => {
      router.push(path, {
        onTransitionReady: slideInOut,
      });
    }, 200);
  };

  return (
    <>
      {/* This nav sits above the overlay (z-[3] > overlay's z-[2]) so the
          toggle button never gets covered, and the icon swap happens
          in place — no separate close button, no position mismatch. */}
      <nav ref={navRef} className="lg:hidden flex items-center justify-end z-[3] relative">
        <div className="flex items-center gap-4">
          <Letstalk />
          <button
            type="button"
            onClick={handleToggleClick}
            aria-label={isOpen ? "Close menu" : "Open menu"}
            aria-expanded={isOpen}
            className="menu-toggle relative w-10 h-10 flex items-center justify-center cursor-pointer overflow-hidden shrink-0"
          >
            <span
              ref={menuOpenTextRef}
              className="absolute inset-0 flex items-center justify-center select-none text-[var(--copy)] [will-change:transform] translate-y-0"
            >
              <RiMenu3Line size={24} />
            </span>
            <span
              ref={menuCloseTextRef}
              className="absolute inset-0 flex items-center justify-center select-none text-[var(--copy)] [will-change:transform] translate-y-full"
            >
              <RiCloseLine size={24} />
            </span>
          </button>
        </div>
      </nav>

      <div
        className="menu-overlay fixed top-0 left-0 w-full h-[100svh] flex justify-center items-center bg-secondary/95 [clip-path:polygon(0%_100%,100%_100%,100%_100%,0%_100%)] [will-change:clip-path] pointer-events-none z-[2]"
        ref={menuOverlayRef}
      >
        <div className="w-full flex flex-col items-center justify-center gap-2 px-6 text-center max-h-[80svh] overflow-y-auto">
          {NAV_LINKS.map((link) => {
            const isDropdown = link.type === "dropdown";
            const isSectionOpen = openSection === link.label;

            return (
              <div
                key={link.label}
                className="menu-link relative w-full flex flex-col items-center overflow-hidden [clip-path:polygon(0_0,100%_0,100%_100%,0%_100%)]"
              >
                {isDropdown ? (
                  <button
                    type="button"
                    onClick={() =>
                      setOpenSection(isSectionOpen ? null : link.label)
                    }
                    aria-expanded={isSectionOpen}
                    className="menu-link-item relative inline-flex items-center gap-2 translate-y-full [will-change:transform]"
                  >
                    <h1 className="uppercase text-4xl sm:text-5xl leading-[1.1] text-[var(--copy)] font-semibold">
                      {link.label}
                    </h1>
                    <RiArrowDownSLine
                      size={26}
                      className={`text-[var(--copy)] transition-transform duration-300 ${
                        isSectionOpen ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                ) : (
                  <a
                    href={link.href}
                    onClick={(e) => handleNavigation(e, link.href)}
                    className="menu-link-item relative inline-block translate-y-full [will-change:transform]"
                  >
                    <h1 className="uppercase text-4xl sm:text-5xl leading-[1.1] text-[var(--copy)] font-semibold">
                      {link.label}
                    </h1>
                  </a>
                )}

                {isDropdown && (
                  <div
                    className={`grid w-full transition-all duration-500 ease-[cubic-bezier(.76,0,.2,1)] ${
                      isSectionOpen
                        ? "grid-rows-[1fr] opacity-100 mt-1"
                        : "grid-rows-[0fr] opacity-0 mt-0"
                    }`}
                  >
                    <ul className="overflow-hidden flex flex-col items-center gap-1 py-2">
                      {link.items.map((item) => (
                        <div key={item.label}>
                          <a
                            href={item.href}
                            onClick={(e) => handleNavigation(e, item.href)}
                            className="block text-base sm:text-lg text-[var(--copy)]/70 hover:text-[var(--copy)] transition-colors py-1"
                          >
                            {item.label}
                          </a>
                        </div>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
};

export default Nav;